import { ASSET_VERSION } from '@arborstoryorg/video-analysis';
import * as EventTracker from '@src/analytics/EventTracker';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import {
  CLIENT_SCENE_DETECTION_PARAMS,
  runClientVideoAnalysis,
  toOverallPercent,
} from './runClientVideoAnalysis';

import type {
  ClientVideoAnalysisPayload,
  ClientVideoAnalysisService,
} from './ClientVideoAnalysisService';

const logger = LogManager.getLogger('service:ClientVideoAnalysisRunner');

/** No progress for this long means a hung worker or GPU: give up. */
export const STALL_TIMEOUT_MS = 60_000;
const SAVE_TIMEOUT_MS = 30_000;

export type ClientVideoAnalysisRunCallbacks = {
  saveResults: ClientVideoAnalysisService['saveResults'];
  /** 0–100, about 4× a second. */
  onProgress: (percent: number) => void;
  /** Called exactly once when the run is over, whatever the outcome. */
  onEnd: () => void;
  /** Whether the original video has finished uploading (for metrics). */
  isUploadLanded: () => boolean;
};

type Phase = 'loading-models' | 'analyzing' | 'saving';

/**
 * Runs client-side scene and face detection for stories uploaded in this
 * session (PLAT-12104 / PLAT-12105), one run per story.
 *
 * A module singleton rather than a hook: the Add Stories dialog unmounts when
 * it is closed mid-upload while the upload carries on, and the story stays
 * blocked from opening until its run ends — so the run must outlive the
 * dialog. Every failure is silent; the server's detection Lambdas still run.
 */
class ClientVideoAnalysisRunner {
  private runs = new Map<string, AbortController>();

  start(
    storyId: string,
    file: File,
    callbacks: ClientVideoAnalysisRunCallbacks,
  ): void {
    if (this.runs.has(storyId)) return;
    const controller = new AbortController();
    this.runs.set(storyId, controller);
    void this.run(storyId, file, controller, callbacks)
      .catch(error => {
        logger.error('client-video-analysis-crashed', {
          storyId,
          ...extractErrorInfo(error),
        });
      })
      .finally(() => {
        this.runs.delete(storyId);
        callbacks.onEnd();
      });
  }

  /** Stop a run (e.g. its upload failed). No-op when none is running. */
  abort(storyId: string): void {
    this.runs.get(storyId)?.abort();
  }

  private async run(
    storyId: string,
    file: File,
    controller: AbortController,
    callbacks: ClientVideoAnalysisRunCallbacks,
  ): Promise<void> {
    const startedAt = performance.now();
    let phase: Phase = 'loading-models';
    let modelLoadMs: number | undefined;
    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const armStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        controller.abort();
      }, STALL_TIMEOUT_MS);
    };
    const elapsed = () => Math.round(performance.now() - startedAt);
    const fail = (errorCode: string, errorMessage?: string) => {
      logger.warn('client-video-analysis-failed', {
        storyId,
        errorCode,
        phase,
        errorMessage,
      });
      EventTracker.trackVideoAnalysisFailed({
        story_id: storyId,
        error_code: errorCode,
        phase,
        elapsed_ms: elapsed(),
        error_message: errorMessage,
      });
    };

    logger.info('client-video-analysis-start', {
      storyId,
      fileSizeBytes: file.size,
    });
    armStallTimer();
    const outcome = await runClientVideoAnalysis(file, {
      signal: controller.signal,
      onProgress: progress => {
        armStallTimer();
        if (progress.phase === 'analyzing' && modelLoadMs === undefined) {
          modelLoadMs = elapsed();
        }
        phase = progress.phase;
        callbacks.onProgress(toOverallPercent(progress));
      },
    });
    clearTimeout(stallTimer);

    if (outcome.status === 'skipped') {
      const reason = outcome.reasons.join('; ') || 'unsupported';
      logger.info('client-video-analysis-skipped', {
        storyId,
        reason,
        codec: outcome.codec,
      });
      EventTracker.trackVideoAnalysisSkipped({
        story_id: storyId,
        reason,
        codec: outcome.codec ?? undefined,
      });
      return;
    }
    if (outcome.status === 'failed') {
      fail(stalled ? 'stalled' : outcome.code, outcome.message);
      return;
    }

    const { summary } = outcome;
    logger.info('client-video-analysis-complete', { storyId, ...summary });
    EventTracker.trackVideoAnalysisCompleted({
      story_id: storyId,
      backend: summary.backend,
      gpu_vendor: summary.gpuVendor ?? undefined,
      elapsed_ms: summary.elapsedMs,
      model_load_ms: modelLoadMs,
      video_duration_s: Math.round(summary.durationSec * 10) / 10,
      realtime_factor:
        summary.elapsedMs > 0
          ? Math.round((summary.durationSec * 1000 * 10) / summary.elapsedMs) /
            10
          : 0,
      width: summary.width,
      height: summary.height,
      codec: summary.codec ?? undefined,
      file_size_mb: Math.round((file.size / (1024 * 1024)) * 100) / 100,
      frames_decoded: summary.framesDecoded,
      frames_sampled: summary.framesSampled,
      faces_detected: summary.facesDetected,
      scene_count: summary.sceneCount,
      people_count: summary.peopleCount,
      finished_before_upload: !callbacks.isUploadLanded(),
    });

    phase = 'saving';
    const payload: ClientVideoAnalysisPayload = {
      scenesCsv: outcome.scenesCsv,
      faceTracks: outcome.faceTracks,
      meta: {
        assetVersion: ASSET_VERSION,
        backend: summary.backend,
        elapsedMs: summary.elapsedMs,
        scenes: { ...CLIENT_SCENE_DETECTION_PARAMS },
      },
    };
    const saveStartedAt = performance.now();
    const saved = await callbacks.saveResults(
      storyId,
      payload,
      AbortSignal.timeout(SAVE_TIMEOUT_MS),
    );
    if (!saved.ok) {
      fail('save_failed', saved.error.message);
      return;
    }
    const saveMs = Math.round(performance.now() - saveStartedAt);
    logger.info('client-video-analysis-saved', {
      storyId,
      saveMs,
      payloadBytes: saved.value.payloadBytes,
    });
    EventTracker.trackVideoAnalysisSaved({
      story_id: storyId,
      save_ms: saveMs,
      payload_kb: Math.round(saved.value.payloadBytes / 1024),
    });
  }

  /** Abort every run and forget it. For testing only. */
  _reset(): void {
    this.runs.forEach(controller => controller.abort());
    this.runs.clear();
  }
}

export const clientVideoAnalysisRunner = new ClientVideoAnalysisRunner();
